import { isPositiveFinite, roundToTwoDecimalPlaces } from "../../../../../common/math/math";
import { formatCurrency } from "../../../../../common/UnitFormatter";
import type { PriceContainerDto, PriceDto } from "../../../../../generated/client/octalog-service";

export const INCOMPLETE_INPUTS_PLACEHOLDER = "-- €";

const PRICE_CONSTANTS = {
  NO_AUTO_PRICE: {
    description: "Keine Preise konfiguriert. Bitte Preis manuell eingeben oder Einstellungen prüfen.",
    requireManualPrice: true,
  } as PriceResult,

  NO_PRICE_AVAILABLE: {
    description: "Für diese Variante ist kein Preis verfügbar",
    requireManualPrice: true,
  } as PriceResult,
} as const;

export const NO_AUTO_PRICE_RESULT: PriceResult = PRICE_CONSTANTS.NO_AUTO_PRICE;

export interface PriceConfig {
  woodTypeId: number;
  humidityLevelId: number;
  cuttingLengthId: number;
}

export interface PriceCalculationProps {
  priceData: PriceContainerDto;
  woodTypeId: number;
  humidityLevelId: number;
  cuttingLengthId: number;
  volume: number;
}

export interface WoodPriceCalculationInput {
  priceData: PriceContainerDto;
  woodTypeId?: number;
  humidityLevelId?: number;
  cuttingLengthId?: number;
  volume?: number;
}

export interface PriceResult {
  price?: number;
  description: string;
  requireManualPrice: boolean;
}

export function areInputsValidForPriceCalculation(
  woodTypeId: number | undefined,
  humidityLevelId: number | undefined,
  cuttingLengthId: number | undefined,
  volume: number | undefined,
): boolean {
  return (
    isPositiveFinite(woodTypeId) &&
    isPositiveFinite(humidityLevelId) &&
    isPositiveFinite(cuttingLengthId) &&
    isPositiveFinite(volume)
  );
}

function findMatchingPrice(prices: PriceDto[], config: PriceConfig): PriceDto | undefined {
  return prices.find(
    (price) =>
      price.woodTypeId === config.woodTypeId &&
      price.humidityLevelId === config.humidityLevelId &&
      price.cuttingLengthId === config.cuttingLengthId,
  );
}

function calculateFinalPrice(basePrice: number, volume: number): PriceResult {
  const totalPrice = roundToTwoDecimalPlaces(basePrice * volume);
  return {
    price: totalPrice,
    description: `${formatCurrency(basePrice)} pro m³`,
    requireManualPrice: false,
  };
}

export function calculateWoodPrice({
  priceData,
  woodTypeId,
  humidityLevelId,
  cuttingLengthId,
  volume,
}: PriceCalculationProps): PriceResult {
  if (priceData.prices.length === 0) {
    return PRICE_CONSTANTS.NO_AUTO_PRICE;
  }

  const priceConfig: PriceConfig = {
    woodTypeId,
    humidityLevelId,
    cuttingLengthId,
  };

  const price = findMatchingPrice(priceData.prices, priceConfig);
  if (!price) {
    return PRICE_CONSTANTS.NO_PRICE_AVAILABLE;
  }

  const basePrice = price.priceOverride ?? price.price;
  return calculateFinalPrice(basePrice, volume);
}

export function calculateWoodPriceOrPlaceholder({
  priceData,
  woodTypeId,
  humidityLevelId,
  cuttingLengthId,
  volume,
}: WoodPriceCalculationInput): PriceResult {
  if (priceData.prices.length === 0) {
    return NO_AUTO_PRICE_RESULT;
  }

  if (!areInputsValidForPriceCalculation(woodTypeId, humidityLevelId, cuttingLengthId, volume)) {
    return { price: undefined, description: INCOMPLETE_INPUTS_PLACEHOLDER, requireManualPrice: false };
  }

  return calculateWoodPrice({
    priceData,
    woodTypeId: woodTypeId!,
    humidityLevelId: humidityLevelId!,
    cuttingLengthId: cuttingLengthId!,
    volume: volume!,
  });
}
